<template>
    <form>
        <ErrorSummary :response-status="responseStatus" />

        <div class="grid grid-cols-4 gap-6 mt-6">
            <div class="col-span-4 sm:col-span-2">
                <label class="block text-sm font-medium text-gray-700" for="vocalPart">Vocal Part
                    <SparklesIcon class="inline w-4 h-4 mx-1 text-powder-600"></SparklesIcon>
                </label>
                <SaCombobox id="vocalPart" v-model="profile.vocalPart" :options="profile.vocalPartOptions"
                    name="vocalPart" />
            </div>

            <div class="col-span-4 sm:col-span-2">
                <label class="block text-sm font-medium text-gray-700" for="dancingLevel">Dance Skill Level
                    <SparklesIcon class="inline w-4 h-4 mx-1 text-powder-600"></SparklesIcon>
                </label>
                <SaCombobox id="dancingLevel" v-model="profile.dancingLevel" :options="profile.dancingLevelOptions"
                    name="dancingLevel" />
            </div>

            <div v-if="displayVocalRanges" class="col-span-4 sm:col-span-2">
                <label class="block text-sm font-medium text-gray-700" for="vocalRangeMin">Vocal Range (Low
                    Note)</label>
                <SaCombobox id="vocalRangeMin" v-model="profile.vocalRangeMin" :options="profile.vocalRangeOptions"
                    name="vocalRangeMin" />
            </div>

            <div v-if="displayVocalRanges" class="col-span-4 sm:col-span-2">
                <label class="block text-sm font-medium text-gray-700" for="vocalRangeMin">Vocal Range (High
                    Note)</label>
                <SaCombobox id="vocalRangeMax" v-model="profile.vocalRangeMax" :options="profile.vocalRangeOptions"
                    name="vocalRangeMax" />
            </div>

        </div>
        <div class="grid md:grid-cols-2 gap-x-8">
            <div class="mt-10 divide-y divide-gray-200">
                <div class="space-y-1">
                    <h3 class="text-xl font-bold leading-6 text-gray-900">Education / Training
                        <SparklesIcon class="inline w-4 h-4 mx-1 text-powder-600"></SparklesIcon>
                    </h3>
                    <p class="max-w-2xl text-sm text-clay-800"></p>
                </div>
                <div class="mt-6">
                    <div class="divide-y divide-gray-200">
                        <div class="grid grid-cols-4 gap-6 mt-6">
                            <div class="col-span-4">
                                <label class="block text-sm font-medium text-gray-700 sr-only"
                                    for="educationOrTraining">List
                                    your education and training</label>
                                <div class="mt-1">
                                    <textarea id="educationOrTraining" v-model="profile.educationOrTraining"
                                        class="block w-full px-3 py-2 mt-1 border rounded-md shadow-sm border-clay-500 focus:outline-hidden focus:ring-clay-800 focus:border-clay-500 sm:text-sm"
                                        name="educationOrTraining" rows="4"></textarea>

                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
            <div class="mt-10 divide-y divide-gray-200">
                <div class="space-y-1">
                    <h3 class="text-xl font-bold leading-6 text-gray-900">Special Skills</h3>
                    <p class="max-w-2xl text-sm text-clay-800"></p>
                </div>
                <div class="mt-6">
                    <div class="divide-y divide-gray-200">
                        <div class="grid grid-cols-4 gap-6 mt-6">
                            <div class="col-span-4">
                                <label class="block text-sm font-medium text-gray-700 sr-only" for="specialSkills">List
                                    your
                                    special skills</label>
                                <div class="mt-1">
                                    <textarea id="specialSkills" v-model="profile.specialSkills"
                                        class="block w-full px-3 py-2 mt-1 border rounded-md shadow-sm border-clay-500 focus:outline-hidden focus:ring-clay-800 focus:border-clay-500 sm:text-sm"
                                        name="specialSkills" rows="4"></textarea>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="py-3 mt-10 text-center">
            <SaButton @click.prevent="submit" :loading="isSubmitting" variant="primary"/>
        </div>
    </form>
    <NotificationSuccess :show="updateSuccessful" content="Your changes were successfully saved."
        title="Successfully saved!" />

</template>

<script lang="ts" setup>
// @ts-ignore
import {computed, onBeforeMount, Ref, ref} from "vue";
import {client} from '@/shared';
import {GetProfileSettingSkills, GetProfileSettingSkillsResponse, UpdateProfileSkills, YourBookmarkDto} from "@/dtos";
// @ts-ignore
import {SparklesIcon, TrashIcon} from '@heroicons/vue/20/solid';
import {ResponseStatus} from "@servicestack/client";
import ErrorSummary from "@/main/components/ErrorSummary.vue";
import SaCombobox from "@/main/components/SaCombobox.vue";
import NotificationSuccess from "@/main/components/NotificationSuccess.vue";
import SaButton from "@/main/components/SaButton.vue";

let responseStatus = ref(null);
let profile: Ref<GetProfileSettingSkillsResponse> = ref(new GetProfileSettingSkillsResponse());
let updateSuccessful: Ref<Boolean> = ref(false);

onBeforeMount(async () => {
    await client.get<GetProfileSettingSkillsResponse>(new GetProfileSettingSkills())
        .then(response => {
            profile.value = response;
        });
});

const displayVocalRanges = computed(() =>{
    return profile.value.vocalPart 
        && profile.value.vocalPart !== '' 
        && profile.value.vocalPart !=='Non-singer';
});

const isSubmitting = ref(false);
const submit = async () => {
    isSubmitting.value = true;
    let request = profile.value;
    await client.post<ResponseStatus>(new UpdateProfileSkills(request))
        .then(_ => {
            updateSuccessful.value = true;
            responseStatus.value = null;
        })
        .catch(e => {
            responseStatus.value = e.responseStatus;
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        });
    isSubmitting.value = false;
};

</script>